import { ChangeDetectorRef, Component, EventEmitter, Input, Output, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';

let nextCropperId = 0;

/**
 * Zoom is measured from the fitted image (1 shows all of it), so a full-size
 * phone photo zooms in the same steps as a small one.
 */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 10;
/** One button press or + / − key. */
const ZOOM_STEP = 1.2;

@Component({
  selector: 'image-cropper-modal',
  templateUrl: './image-cropper-modal.component.html',
  styleUrls: ['./image-cropper-modal.component.css'],
  standalone: true,
  // cdkTrapFocus keeps Tab inside the dialog, moves focus in on open and
  // returns it to where it was (the file input) when the dialog closes.
  imports: [CommonModule, CdkTrapFocus],
})
export class ImageCropperModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @Input() imageUrl: string = '';
  @Output() imageCropped = new EventEmitter<Blob>();
  @Output() cancelled = new EventEmitter<void>();
  /** The browser could not open the image (e.g. a HEIC photo outside Safari). */
  @Output() failed = new EventEmitter<void>();

  private cdr = inject(ChangeDetectorRef);

  readonly titleId = `image-cropper-title-${nextCropperId++}`;
  readonly hintId = `${this.titleId}-hint`;

  /** True from the first Save click until the parent closes the dialog. */
  saving = false;
  /** The image is decoded and drawn. Until then Save would upload an empty square. */
  ready = false;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private img: HTMLImageElement = new Image();

  // Image transformation state
  scale: number = 1;
  offsetX: number = 0;
  offsetY: number = 0;

  // Dragging state
  private isDragging: boolean = false;
  private lastX: number = 0;
  private lastY: number = 0;

  // Canvas dimensions
  private canvasSize: number = 400;
  /** The scale that fits the whole image in the square. */
  private fitScale = 1;

  /** Zoom as people read it: 100% shows the whole image. */
  get zoomPercent(): number {
    return Math.round((this.scale / this.fitScale) * 100);
  }

  ngOnInit(): void {
    this.img.onload = () => this.onImageReady();
    this.img.onerror = () => this.failed.emit();
    this.img.src = this.imageUrl;
  }

  ngAfterViewInit(): void {
    this.canvas = this.canvasRef.nativeElement;
    this.ctx = this.canvas.getContext('2d')!;

    // Decoded before the view existed: draw it after this check, not during it.
    if (this.img.complete && this.img.naturalWidth) {
      Promise.resolve().then(() => this.onImageReady());
    }
  }

  /** The image is decoded and the canvas exists: fit the image and allow saving. */
  private onImageReady(): void {
    if (this.ready || !this.canvas || !this.ctx) return;
    this.initializeCanvas();
    this.ready = true;
    this.cdr.markForCheck();
  }

  private initializeCanvas(): void {
    if (!this.canvas || !this.ctx || !this.img.naturalWidth) return;

    // Set canvas size
    this.canvas.width = this.canvasSize;
    this.canvas.height = this.canvasSize;

    // Calculate initial scale to fit image in canvas
    const imgAspect = this.img.width / this.img.height;
    const canvasAspect = this.canvas.width / this.canvas.height;

    if (imgAspect > canvasAspect) {
      this.scale = this.canvas.width / this.img.width;
    } else {
      this.scale = this.canvas.height / this.img.height;
    }

    this.fitScale = this.scale;

    // Center the image
    this.offsetX = (this.canvas.width - this.img.width * this.scale) / 2;
    this.offsetY = (this.canvas.height - this.img.height * this.scale) / 2;

    this.drawImage();
  }

  private drawImage(): void {
    if (!this.ctx || !this.canvas) return;

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw image with current transformations
    this.ctx.save();
    this.ctx.drawImage(
      this.img,
      this.offsetX,
      this.offsetY,
      this.img.width * this.scale,
      this.img.height * this.scale
    );
    this.ctx.restore();
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    // The canvas is drawn at 400px but may be shown smaller on phones, so
    // convert screen pixels to canvas pixels to keep the image under the cursor.
    const k = this.pointerScale();
    const deltaX = (event.clientX - this.lastX) * k;
    const deltaY = (event.clientY - this.lastY) * k;

    this.offsetX += deltaX;
    this.offsetY += deltaY;

    this.lastX = event.clientX;
    this.lastY = event.clientY;

    this.drawImage();
  }

  onMouseUp(): void {
    this.isDragging = false;
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      this.isDragging = true;
      this.lastX = event.touches[0].clientX;
      this.lastY = event.touches[0].clientY;
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging || event.touches.length !== 1) return;

    event.preventDefault();

    const k = this.pointerScale();
    const deltaX = (event.touches[0].clientX - this.lastX) * k;
    const deltaY = (event.touches[0].clientY - this.lastY) * k;

    this.offsetX += deltaX;
    this.offsetY += deltaY;

    this.lastX = event.touches[0].clientX;
    this.lastY = event.touches[0].clientY;

    this.drawImage();
  }

  onTouchEnd(): void {
    this.isDragging = false;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();

    // In proportion to how far the wheel or trackpad moved; line-based wheels report lines.
    const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;

    // Zoom towards the mouse position
    const rect = this.canvas.getBoundingClientRect();
    const k = this.pointerScale();
    this.zoomTo(this.scale * Math.exp(-pixels * 0.002), (event.clientX - rect.left) * k, (event.clientY - rect.top) * k);
  }

  zoomIn(): void {
    this.zoomTo(this.scale * ZOOM_STEP, this.canvas.width / 2, this.canvas.height / 2);
  }

  zoomOut(): void {
    this.zoomTo(this.scale / ZOOM_STEP, this.canvas.width / 2, this.canvas.height / 2);
  }

  /** Zooms to `next` (kept within the limits) while the canvas point (x, y) stays put. */
  private zoomTo(next: number, x: number, y: number): void {
    if (!this.ready) return;
    const newScale = Math.min(this.fitScale * MAX_ZOOM, Math.max(this.fitScale * MIN_ZOOM, next));

    this.offsetX = x - (x - this.offsetX) * (newScale / this.scale);
    this.offsetY = y - (y - this.offsetY) * (newScale / this.scale);

    this.scale = newScale;
    this.drawImage();
  }

  resetZoom(): void {
    this.initializeCanvas();
  }

  /** Arrow keys move the image (Shift for bigger steps), + and - zoom, 0 resets. */
  onCanvasKeydown(event: KeyboardEvent): void {
    const step = event.shiftKey ? 50 : 10;
    switch (event.key) {
      case 'ArrowLeft':
        this.offsetX -= step;
        break;
      case 'ArrowRight':
        this.offsetX += step;
        break;
      case 'ArrowUp':
        this.offsetY -= step;
        break;
      case 'ArrowDown':
        this.offsetY += step;
        break;
      case '+':
      case '=':
        event.preventDefault();
        this.zoomIn();
        return;
      case '-':
      case '_':
        event.preventDefault();
        this.zoomOut();
        return;
      case '0':
        event.preventDefault();
        this.resetZoom();
        return;
      default:
        return;
    }
    event.preventDefault();
    this.drawImage();
  }

  cropImage(): void {
    // toBlob is async and the dialog only closes once the parent handles the
    // blob, so a double click would otherwise upload the image twice.
    if (!this.canvas || this.saving || !this.ready) return;
    this.saving = true;

    this.canvas.toBlob((blob) => {
      if (blob) {
        this.imageCropped.emit(blob);
      } else {
        this.saving = false; // nothing was emitted: let the user try again
      }
    }, 'image/png');
  }

  cancel(): void {
    this.cancelled.emit();
  }

  /** Escape closes without saving, like the close button. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.cancel();
  }

  /** Canvas pixels per CSS pixel (1 when the canvas is shown at full size). */
  private pointerScale(): number {
    const rect = this.canvas?.getBoundingClientRect();
    return rect && rect.width ? this.canvas.width / rect.width : 1;
  }

  ngOnDestroy(): void {
    // Cleanup. Handlers first: clearing src counts as a failed load in some browsers.
    this.img.onload = null;
    this.img.onerror = null;
    this.img.src = '';
  }
}
